import styled, { css, keyframes } from 'styled-components';
import { colors, motion, radii, spacing, typography } from '../../../../theme-v2';
import { AddonLayout } from '../AddonLayout';

/**
 * Quiz accent. The Quiz sets `--quiz-accent` from the AntD `colorPrimary`, so
 * these surfaces track the button. Falls back to the theme-v2 accent when unset.
 */
const QUIZ_ACCENT = `var(--quiz-accent, ${colors.accent.base})`;

/** Per-answer visual state once a question has been answered. */
export type AnswerState = 'idle' | 'correct' | 'wrong' | 'reveal';

const correctPulse = keyframes`
  0% { transform: scale(1); }
  40% { transform: scale(1.025); }
  100% { transform: scale(1); }
`;

const wrongShake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  60% { transform: translateX(-3px); }
  80% { transform: translateX(3px); }
`;

const starPop = keyframes`
  0% { transform: scale(0) rotate(-20deg); opacity: 0; }
  60% { transform: scale(1.35) rotate(8deg); opacity: 1; }
  100% { transform: scale(1) rotate(0); opacity: 1; }
`;

export const StyledWrapper = styled(AddonLayout)`
  background: ${colors.surface.base};
  border-radius: ${radii.box}px;
`;

export const StyledState = styled(AddonLayout).attrs({
  align: 'center',
  justify: 'center',
})`
  min-height: 160px;
  padding: ${spacing.lg}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  color: ${colors.text.secondary};
  text-align: center;
`;

export const StyledCentered = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.base}px;
  padding: ${spacing.lg}px;
  text-align: center;
`;

export const StyledStartTitle = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  color: ${colors.text.primary};
`;

export const StyledStartSubtitle = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  color: ${colors.text.tertiary};
`;

/* Grows so the footer sits at the bottom of a short tab. */
export const StyledBody = styled.div`
  flex: 1 0 auto;
  padding: ${spacing.lg}px;
`;

export const StyledProgress = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${QUIZ_ACCENT};
  margin-bottom: ${spacing.xs}px;
`;

export const StyledTrack = styled.div`
  height: 4px;
  border-radius: ${radii.xs}px;
  background: ${colors.border.subtle};
  margin-bottom: ${spacing.base}px;
  overflow: hidden;
`;

export const StyledFill = styled.div`
  height: 100%;
  background: ${QUIZ_ACCENT};
  border-radius: ${radii.xs}px;
  transition: width ${motion.duration.base} ${motion.easing.standard};
`;

export const StyledQuestion = styled.h3`
  margin: 0 0 ${spacing.base}px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.lineHeight.tight};
  color: ${colors.text.primary};
`;

export const StyledAnswers = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
`;

const answerColors: Record<
  AnswerState,
  { bg: string; border: string }
> = {
  idle: { bg: colors.surface.base, border: colors.border.subtle },
  correct: { bg: colors.status.successBg, border: colors.status.success },
  reveal: { bg: colors.status.successBg, border: colors.status.success },
  wrong: { bg: colors.status.errorBg, border: colors.status.error },
};

/** Reveal animation for an answered option (none while idle). */
const answerAnimation = ($state: AnswerState) => {
  if ($state === 'correct')
    return css`
      animation: ${correctPulse} 0.35s ease;
    `;
  if ($state === 'wrong')
    return css`
      animation: ${wrongShake} 0.4s ease;
    `;
  return '';
};

export const StyledAnswer = styled.button<{ $state: AnswerState }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm}px;
  width: 100%;
  text-align: left;
  padding: ${spacing.md}px;
  border-radius: ${radii.box}px;
  border: 1px solid ${({ $state }) => answerColors[$state].border};
  background: ${({ $state }) => answerColors[$state].bg};
  color: ${colors.text.primary};
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  cursor: pointer;
  transition:
    border-color ${motion.duration.fast} ${motion.easing.standard},
    background ${motion.duration.fast} ${motion.easing.standard};

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    border-color: ${colors.accent.border};
    background: ${colors.accent.tintSubtle};
  }

  ${({ $state }) => answerAnimation($state)}

  svg {
    flex: none;
    color: ${({ $state }) =>
      $state === 'wrong' ? colors.status.error : colors.status.success};
  }
`;

export const StyledFooter = styled.div`
  padding: ${spacing.md}px ${spacing.lg}px;
  border-top: 1px solid ${colors.border.subtle};
  background: ${colors.surface.base};
`;

export const StyledScore = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size['3xl']}px;
  font-weight: ${typography.weight.bold};
  color: ${QUIZ_ACCENT};
  letter-spacing: ${typography.letterSpacing.tight};
`;

export const StyledScoreMessage = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.md}px;
  color: ${colors.text.secondary};
  line-height: ${typography.lineHeight.body};
`;

export const StyledStars = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.xs}px;
  max-width: 100%;
`;

export const StyledStar = styled.span<{ $earned: boolean; $index: number }>`
  display: inline-flex;
  color: ${({ $earned }) =>
    $earned ? QUIZ_ACCENT : colors.text.quaternary};
  ${({ $earned, $index }) =>
    $earned
      ? css`
          opacity: 0;
          animation: ${starPop} 0.45s
            cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
          animation-delay: ${$index * 0.12}s;
        `
      : ''}
`;
